import { useId, useState } from 'react';
import { Badge } from 'ui/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'ui/components/ui/tooltip';
import type { SellerBadges } from '@util/sellerBadges/computeSellerBadges';
import { cn } from 'ui/lib/utils';

// Exact wording approved by Bill, 2026-11-03 11:18:30 CST (mail 11878).
const badgeCopy = [
  {
    key: 'topRated',
    name: 'Top Rated',
    tooltip:
      'Buyers love this seller. 5 or more reviews from at least 3 different buyers, averaging 4.8 stars or higher.',
  },
  {
    key: 'greatCommunication',
    name: 'Great Communication',
    tooltip:
      'Buyers say this seller communicates well. 80% or more of their reviews rate communication Excellent, across at least 5 reviews from 3 different buyers.',
  },
] as const;

export default function SellerServiceBadges({ badges, className }: { badges: SellerBadges; className?: string }) {
  const tooltipId = useId();
  const [openBadge, setOpenBadge] = useState<keyof SellerBadges | null>(null);
  const earned = badgeCopy.filter((badge) => badges[badge.key]);
  // No wrapper, margin, or empty box when the seller has earned no award.
  if (earned.length === 0) return null;

  return (
    <TooltipProvider>
      <div className={cn('flex flex-wrap items-center gap-[0.6rem] whitespace-normal', className)}>
        {earned.map((badge) => (
          <Tooltip
            key={badge.key}
            open={openBadge === badge.key}
            onOpenChange={(open) => setOpenBadge((current) =>
              open ? badge.key : current === badge.key ? null : current
            )}
          >
            <TooltipTrigger
              type="button"
              closeOnClick={false}
              onClick={() => setOpenBadge(badge.key)}
              aria-describedby={`${tooltipId}-${badge.key}`}
              className="rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <Badge
                variant="outline"
                className="h-auto whitespace-normal border-border bg-muted px-[0.8rem] py-[0.4rem] text-[1.2rem] text-foreground"
              >
                {badge.name}
              </Badge>
            </TooltipTrigger>
            <TooltipContent
              id={`${tooltipId}-${badge.key}`}
              role="tooltip"
              className="max-w-[28rem] px-[1.2rem] py-[0.8rem] text-[1.2rem]"
            >
              {badge.tooltip}
            </TooltipContent>
          </Tooltip>
        ))}
      </div>
    </TooltipProvider>
  );
}
